import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Helmet } from 'react-helmet';
import ChatBubble from '../components/ChatBubble';
import ChatInput from '../components/ChatInput';
import './PublicChat.css';

// Get API URL from environment
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : process.env.REACT_APP_API_URL || 'http://localhost:8040';

const PublicChat = () => {
  const [messages, setMessages] = useState([
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newMessageAlert, setNewMessageAlert] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadedViaButton, setLoadedViaButton] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingTimeoutId, setPollingTimeoutId] = useState(null);
  const [passcode, setPasscode] = useState(null);
  const [clinicName, setClinicName] = useState('Public Chat');

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messageListRef = useRef(null);

  // Extract passcode from URL query parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const passcodeParam = urlParams.get('p');

    if (passcodeParam) {
      setPasscode(passcodeParam);
    } else {
      setError('Passcode is required. Please provide a passcode in the URL query parameters.');
    }
  }, []);

  // API functions
  const fetchMessages = async (beforeId = null) => {
    if (!passcode) {
      return { messages: [], hasMore: false };
    }

    try {
      const url = `${API_URL}/api/chat/${passcode}${beforeId ? `?before_id=${beforeId}` : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.status}`);
      }

      const data = await response.json();
      return {
        messages: data.messages || [],
        hasMore: data.has_more !== false // Default to true if not specified
      };
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
      return {
        messages: [],
        hasMore: false
      };
    }
  };

  const fetchClinicInfo = async () => {
    if (!passcode) {
      return;
    }

    try {
      const url = `${API_URL}/api/chat/${passcode}/clinic`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch clinic info: ${response.status}`);
      }

      const data = await response.json();
      if (data.clinic && data.clinic.name) {
        setClinicName(data.clinic.name);
      }
    } catch (error) {
      console.error('Error fetching clinic info:', error);
      // Don't set error state for clinic info failure, just keep default name
    }
  };

  const sendMessage = async (messageContent) => {
    if (!passcode) {
      throw new Error('Passcode is required');
    }

    try {
      const url = `${API_URL}/api/chat/${passcode}/send`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message_content: messageContent,
          message_type: 'text'
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  // Polling function to check for new assistant messages
  const pollForResponse = async (lastKnownMessageId, attempts = 0, maxAttempts = 30) => {
    if (attempts >= maxAttempts) {
      console.log('Polling stopped: max attempts reached');
      setIsPolling(false);
      return;
    }

    try {
      // Fetch recent messages (without before_id to get the latest messages)
      const result = await fetchMessages();
      const newMessages = result.messages || [];

      // Get current message IDs to compare against
      const currentMessageIds = new Set(messages.map(m => m.id));

      // Find messages that we don't have locally (excluding temp messages)
      const trulyNewMessages = newMessages.filter(msg =>
        !currentMessageIds.has(msg.id) && !msg.id.startsWith('temp-')
      );

      // Check if there are any new non-user messages
      const newNonUserMessages = trulyNewMessages.filter(msg => msg.role !== 'user');

      if (newNonUserMessages.length > 0) {
        // Found new assistant/system messages, update the messages
        setMessages(prevMessages => {
          const systemMessages = prevMessages.filter(m => m.role === 'system');
          const otherMessages = prevMessages.filter(m => m.role !== 'system');

          // Remove temp messages and merge with fresh data from server
          const filteredMessages = otherMessages.filter(m => !m.id.startsWith('temp-'));

          // Create a map of existing messages by ID for efficient lookup
          const existingMessagesMap = new Map(filteredMessages.map(m => [m.id, m]));

          // Merge new messages with existing ones, avoiding duplicates
          const allNonSystemMessages = [...newMessages.filter(m => m.role !== 'system')];
          const uniqueMessages = allNonSystemMessages.filter((msg, index, arr) =>
            arr.findIndex(m => m.id === msg.id) === index
          );

          return [...uniqueMessages, ...systemMessages];
        });

        setLastMessageId(newMessages[newMessages.length - 1]?.id);
        setIsPolling(false);
        console.log('Polling stopped: new assistant message received');
        return;
      }

      // No new assistant message yet, continue polling with exponential backoff
      const delay = attempts === 0 ? 5000 : Math.min(2000 + (attempts * 500), 5000); // Start at 5s, increase by 500ms each attempt, max 5s
      const timeoutId = setTimeout(() => {
        pollForResponse(lastKnownMessageId, attempts + 1, maxAttempts);
      }, delay);

      setPollingTimeoutId(timeoutId);

    } catch (error) {
      console.error('Error during polling:', error);
      setIsPolling(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutId) {
        clearTimeout(pollingTimeoutId);
      }
    };
  }, [pollingTimeoutId]);

  // Load initial messages
  useEffect(() => {
    if (!passcode) return; // Don't load messages if passcode is not available

    const loadInitialMessages = async () => {
      setIsLoading(true);

      // Fetch clinic info first
      await fetchClinicInfo();

      const result = await fetchMessages();

      if (result.messages.length > 0) {
        setMessages(prevMessages => {
          const systemMessage = prevMessages.find(m => m.role === 'system');
          const newMessages = result.messages; // Remove .reverse() to match ConversationDetail.jsx
          setLastMessageId(newMessages[newMessages.length - 1]?.id);
          return [...newMessages, ...(systemMessage ? [systemMessage] : [])];
        });
      }
      setHasMoreMessages(result.hasMore);
      setIsLoading(false);
    };

    loadInitialMessages();
  }, [passcode]); // Add passcode as dependency

  useEffect(() => {
    // Scroll to bottom when messages update
    if (!newMessageAlert && !loadedViaButton) {
      scrollToBottom();
    }
  }, [messages, loadedViaButton]);

  const scrollToBottom = () => {
    if (!loadedViaButton) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  };

  // Handle scroll events in the message list
  const handleScroll = () => {
    if (messageListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messageListRef.current;
      const isScrolledToBottom = scrollHeight - scrollTop - clientHeight < 50;

      // Clear new message alert when scrolled to bottom
      if (isScrolledToBottom && newMessageAlert) {
        setNewMessageAlert(false);
      }
    }
  };

  // Handle viewport resizing when virtual keyboard appears on mobile
  useEffect(() => {
    const handleResize = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.style.height = `${window.innerHeight}px`;
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get the last non-user message ID before sending
      const lastNonUserMessage = messages
        .filter(m => m.role !== 'user' && m.role !== 'system')
        .slice(-1)[0];
      const lastNonUserMessageId = lastNonUserMessage?.id;

      await sendMessage(text);

      // Add the message locally for immediate feedback
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message_content: text,
        role: 'user',
        timestamp: new Date().toISOString()
      };

      setMessages(prevMessages => {
        const systemMessages = prevMessages.filter(m => m.role === 'system');
        const otherMessages = prevMessages.filter(m => m.role !== 'system');
        return [...otherMessages, tempMessage, ...systemMessages];
      });

      // Start polling for assistant response
      setIsPolling(true);
      pollForResponse(lastNonUserMessageId);

    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');

      // Add a local error message
      const errorMessage = {
        id: Date.now().toString(),
        message_content: "Failed to send message. Please try again.",
        role: "system",
        timestamp: new Date().toISOString()
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = async (buttonValue, buttonText) => {
    // Send the button value as the message content
    await handleSendMessage(buttonValue);
  };

  const loadMoreMessages = async () => {
    if (isLoadingMore || !hasMoreMessages) return;

    setIsLoadingMore(true);
    setLoadedViaButton(true);

    try {
      // Get the oldest message ID for pagination
      const oldestMessageId = messages.length > 0 ? messages[0].id : null;
      const result = await fetchMessages(oldestMessageId);

      if (result.messages.length > 0) {
        setMessages(prevMessages => {
          const systemMessages = prevMessages.filter(m => m.role === 'system');
          const otherMessages = prevMessages.filter(m => m.role !== 'system');
          const newMessages = result.messages;
          return [...newMessages, ...otherMessages, ...systemMessages];
        });
      }

      setHasMoreMessages(result.hasMore);
    } catch (error) {
      console.error('Error loading more messages:', error);
      setError('Failed to load more messages');
    } finally {
      setIsLoadingMore(false);
      // // Reset the flag after a short delay to allow normal scrolling again
      // setTimeout(() => setLoadedViaButton(false), 5000);
    }
  };

  // Function to format date for date separators
  const formatDateSeparator = (date) => {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString([], {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // Function to check if we need a date separator
  const needsDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;

    const currentDate = new Date(currentMessage.timestamp).toDateString();
    const previousDate = new Date(previousMessage.timestamp).toDateString();

    return currentDate !== previousDate;
  };

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <meta name="theme-color" content="#285D54" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <title>{clinicName}</title>
      </Helmet>

      <div ref={chatContainerRef} className="flex flex-col h-screen bg-gray-100 overflow-hidden chat-container">
        <div className="bg-[rgb(40,93,84)] text-white p-3 shadow-md no-select">
          <div className="container mx-auto flex flex-wrap justify-between items-center gap-2">
            <h1 className="text-xl font-bold">{clinicName}</h1>
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
                  error ? 'bg-red-400' :
                  isPolling ? 'bg-yellow-400' :
                  'bg-green-400'
                }`}></span>
                <span>
                  {error ? 'Error' :
                   isPolling ? 'Waiting for response...' :
                   'Connected'}
                </span>
                {isPolling && (
                  <div className="inline-block w-3 h-3 border border-white border-t-transparent rounded-full animate-spin ml-2"></div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mx-3 mt-3 rounded">
            {error}
          </div>
        )}

        <div
          ref={messageListRef}
          className="flex-1 overflow-y-auto p-3 md:p-4 container mx-auto max-w-4xl chat-message-list"
          onScroll={handleScroll}
        >
          <div className="space-y-3">
            {/* Load more messages button - only show if we have messages and hasMoreMessages is true */}
            {messages.length > 0 && hasMoreMessages && (
              <div className="flex justify-center mb-4">
                <button
                  onClick={loadMoreMessages}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded flex items-center"
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                      Load more messages
                    </>
                  )}
                </button>
              </div>
            )}

            {messages.map((message, index) => (
              <React.Fragment key={message.id}>
                {/* Date separator */}
                {needsDateSeparator(message, messages[index - 1]) && (
                  <div className="flex justify-center my-4">
                    <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                      {formatDateSeparator(message.timestamp)}
                    </div>
                  </div>
                )}

                <ChatBubble
                  message={{
                    ...message,
                    text: message.message_content, // Keep text for backward compatibility
                    sender: message.role === 'user' ? (message.from_number || 'User') : message.role,
                    timestamp: new Date(message.timestamp)
                  }}
                  isOwnMessage={message.role === 'user' && (message.from_number || message.id.startsWith('temp-'))}
                  onQuickReply={handleQuickReply}
                />
              </React.Fragment>
            ))}

            {/* Typing indicator when polling */}
            {isPolling && (
              <div className="flex justify-start mb-4">
                <div className="bg-white rounded-lg px-4 py-2 shadow-sm border max-w-xs">
                  <div className="flex items-center space-x-1">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2">Assistant is typing...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {newMessageAlert && (
          <div
            className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-[rgb(40,93,84)] text-white px-4 py-2 rounded-full shadow-lg cursor-pointer"
            onClick={() => {
              scrollToBottom();
              setNewMessageAlert(false);
            }}
          >
            New messages ↓
          </div>
        )}

        <div className="border-t border-gray-200 bg-white sticky bottom-0 p-3 shadow-md safe-area-padding">
          <div className="container mx-auto max-w-4xl">
            <ChatInput onSendMessage={handleSendMessage} disabled={isLoading || isPolling || !passcode} />
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicChat;

